// src/components/VideoGallery.jsx
import { useState, useEffect } from 'react';
import axios from 'axios';
import { Play, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

function VideoCard({ video, onClick }) {
  return (
    <div
      onClick={() => onClick(video)}
      className="
        group relative
        aspect-[9/16] overflow-hidden rounded-xl
        bg-gradient-to-b from-gray-900 to-black
        cursor-pointer shadow-lg hover:shadow-2xl
        transition-all duration-300 hover:scale-[1.03]
        border border-gray-800
      "
    >
      {/* Video thumbnail / poster */}
      <img
        src={video.thumbnail || video.videoUrl} // fallback to video if no thumbnail
        alt={video.description?.substring(0, 40) || 'Video'}
        className="
          absolute inset-0 h-full w-full object-cover
          brightness-90 group-hover:brightness-100
          transition-all duration-500
        "
        loading="lazy"
      />

      {/* Bottom gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent pointer-events-none" />

      {/* Play button center */}
      <div className="
        absolute inset-0 flex items-center justify-center
        opacity-90 group-hover:opacity-100
        transition-opacity duration-300
      ">
        <div className="
          flex h-16 w-16 items-center justify-center
          rounded-full bg-emerald-600/90 backdrop-blur-md
          shadow-xl shadow-black/50
          group-hover:scale-110 group-hover:bg-emerald-500
          transition-all duration-300
        ">
          <Play className="ml-1 h-8 w-8 fill-white text-white" />
        </div>
      </div>

      {/* Title / Description at bottom */}
      <div className="absolute bottom-0 left-0 right-0 p-4">
        <h3 className="text-base md:text-lg font-semibold text-white leading-tight drop-shadow-md line-clamp-2">
          {video.description?.substring(0, 60) || 'Untitled Video'}
          {video.description?.length > 60 ? '...' : ''}
        </h3>

        {/* Optional: show company name if populated */}
        {video.user?.companyName && (
          <p className="mt-1 text-xs text-gray-300">
            {video.user.companyName}
          </p>
        )}
      </div>
    </div>
  );
}

function VideoModal({ video, onClose }) {
  if (!video) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 p-4"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-5xl rounded-2xl overflow-hidden bg-black shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="
            absolute right-4 top-4 z-10
            rounded-full bg-black/70 p-3 text-white
            hover:bg-gray-800 transition-colors
          "
        >
          <X size={28} />
        </button>

        {/* Video player */}
        <video
          src={video.videoUrl}
          controls
          autoPlay
          className="w-full aspect-video bg-black"
        >
          <source src={video.videoUrl} type="video/mp4" />
          Your browser does not support the video tag.
        </video>

        {/* Info below video */}
        <div className="p-6 bg-gradient-to-t from-black to-transparent">
          <h2 className="text-xl md:text-2xl font-bold text-white">
            {video.description?.substring(0, 100) || 'Video'}
          </h2>
          {video.user?.companyName && (
            <p className="mt-2 text-gray-400">
              Posted by {video.user.companyName}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export default function VideoGallery() {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVideo, setSelectedVideo] = useState(null);

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API_URL}videos`);
        setVideos(response.data);
      } catch (err) {
        console.error('Error fetching videos:', err);
        toast.error('Could not load videos');
      } finally {
        setLoading(false);
      }
    };

    fetchVideos();
  }, []);

  if (loading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-10 w-10 animate-spin text-emerald-500" />
          <p className="text-gray-400">Loading videos...</p>
        </div>
      </div>
    );
  }

  if (videos.length === 0) {
    return (
      <div className="text-center py-16 text-gray-400">
        No videos found yet.
      </div>
    );
  }

  return (
    <>
      <div className="
        grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5
        gap-3 sm:gap-4 md:gap-5
      ">
        {videos.map((video, index) => (
          <VideoCard
            key={video._id || index}
            video={video}
            onClick={setSelectedVideo}
          />
        ))}
      </div>

      <VideoModal
        video={selectedVideo}
        onClose={() => setSelectedVideo(null)}
      />
    </>
  );
}